'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Capacitor } from '@capacitor/core';
import {
  initAppsFlyer,
  setDeepLinkNavigator,
  clearDeepLinkNavigator,
  setAppsFlyerUserId,
} from '@/lib/appsflyer';
import { initFacebookSdk } from '@/lib/facebook';
// Shared website-host allowlist (also used by notification deeplink validation).
// A verified https://docupro.in/... App Link opens the app and is delivered via
// Capacitor's appUrlOpen — we route its path into the Next router below. OneLink
// (docupro.onelink.me) URLs are intentionally excluded: the AppsFlyer SDK
// resolves those itself via Unified Deep Linking.
import { WEBSITE_APP_LINK_HOSTS } from '@/lib/safe-path';

/**
 * Boots the AppsFlyer SDK once and bridges incoming OneLink deep links into
 * the Next.js router, so a tapped/deferred link opens the target screen
 * *inside the app* rather than the system browser.
 *
 * Mount exactly once, near the app root (inside the native provider tree).
 * On web every underlying call is a no-op.
 */
export function AppsFlyerProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { data: session } = useSession();
  const userId = session?.user?.id;

  useEffect(() => {
    // Register the navigator first so a deferred deep link that resolves
    // during/just after init is replayed into the router immediately.
    setDeepLinkNavigator((path) => router.push(path));
    void initAppsFlyer();
    void initFacebookSdk();

    // Route plain website App Links (https://docupro.in/...) into the app.
    // When a verified App Link opens the app, Capacitor delivers the URL here
    // instead of navigating the WebView, so we push the path ourselves.
    const routeWebsiteUrl = (url?: string | null) => {
      if (!url) return;
      try {
        const u = new URL(url);
        if (!WEBSITE_APP_LINK_HOSTS.has(u.host)) return; // onelink.me → AppsFlyer
        const path = u.pathname + u.search + u.hash;
        if (path && path !== '/') router.push(path);
      } catch {
        /* ignore malformed URLs */
      }
    };

    let removeUrlListener: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      void (async () => {
        const { App } = await import('@capacitor/app');
        // Cold start: the app was launched directly by tapping the link.
        const launch = await App.getLaunchUrl();
        routeWebsiteUrl(launch?.url);
        // Warm start: a link tapped while the app is already open.
        const handle = await App.addListener('appUrlOpen', ({ url }) =>
          routeWebsiteUrl(url)
        );
        removeUrlListener = () => void handle.remove();
      })();
    }

    return () => {
      clearDeepLinkNavigator();
      removeUrlListener?.();
    };
    // router identity is stable across renders in the app router.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tie AppsFlyer events to the logged-in user so referral attribution and
  // funnel analytics are associated with a real account. No-op on web.
  useEffect(() => {
    if (userId) void setAppsFlyerUserId(userId);
  }, [userId]);

  return <>{children}</>;
}
